/**
 * GA4 (G-L5JWN77BGV) — same web stream as tagada.io / tagadapay.io / dashboard.
 * Product funnels stay in PostHog. This is acquisition only.
 *
 * Two products share this Clerk host. Every conversion carries `product` +
 * `funnel` so a CRM Start-free signup is never mixed with a TagadaPay apply.
 */

export const GA_MEASUREMENT_ID = 'G-L5JWN77BGV';

export type GaProduct = 'crm' | 'payfac';

const PRODUCT_STORAGE = 'tagada_ga_product';
const IDENTITY_STORAGE = 'tagada_ga_identity';
const SIGNUP_GUARD = 'ga-signup-fired';
const EVENT_PARAM_MAX = 100;

const IDENTITY_KEYS = [
  'first_name',
  'last_name',
  'email',
  'account_name',
  'organization',
  'clerk_user_id',
] as const;

export type GaIdentity = Partial<Record<(typeof IDENTITY_KEYS)[number], string>>;

const ATTR_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'twclid',
  'ref',
  'product',
  'source',
]);

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Prod Tagada CRM only — never white-label, preview, staging, or local. */
export const shouldTrackGa = (): boolean => {
  if (typeof window === 'undefined' || import.meta.env.DEV) return false;
  const brand = (import.meta.env.VITE_BRAND as string | undefined)?.toLowerCase();
  if (brand && brand !== 'tagadapay') return false;
  return window.location.hostname === 'app.tagada.io';
};

/** Keep attribution params; drop everything else (CRM search is customer PII). */
export const sanitizeGaLocation = (href: string): string => {
  const url = new URL(href);
  for (const key of [...url.searchParams.keys()]) {
    if (!ATTR_KEYS.has(key)) url.searchParams.delete(key);
  }
  url.hash = '';
  return url.toString();
};

function pathImpliesPayfac(pathname: string): boolean {
  return (
    pathname.startsWith('/dashboard/sign-up') ||
    pathname.startsWith('/accept-invitation') ||
    pathname.startsWith('/accept-sign-in')
  );
}

/** First-touch product for this tab. URL / apply handoff wins over default CRM. */
export const persistGaProductFromLocation = (): void => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const queryProduct = params.get('product');
  const source = params.get('source');
  let product: GaProduct | null = null;
  if (queryProduct === 'crm' || queryProduct === 'payfac') product = queryProduct;
  else if (source === 'tagadapay') product = 'payfac';
  else if (pathImpliesPayfac(window.location.pathname)) product = 'payfac';
  if (!product) return;
  try {
    sessionStorage.setItem(PRODUCT_STORAGE, product);
  } catch {
    // private mode
  }
};

function clipGaValue(value: string): string {
  return value.trim().slice(0, EVENT_PARAM_MAX);
}

export const readGaIdentity = (): GaIdentity => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(IDENTITY_STORAGE);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as GaIdentity;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/** Merge newly known session fields. Empty values are ignored so we never wipe a filled field. */
export const setGaIdentity = (partial: GaIdentity): GaIdentity => {
  const next = { ...readGaIdentity() };
  for (const key of IDENTITY_KEYS) {
    const value = partial[key];
    if (typeof value === 'string' && value.trim()) next[key] = clipGaValue(value);
  }
  try {
    sessionStorage.setItem(IDENTITY_STORAGE, JSON.stringify(next));
  } catch {
    // private mode
  }
  if (next.clerk_user_id && typeof window.gtag === 'function') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: next.clerk_user_id,
      send_page_view: false,
    });
  }
  return next;
};

export const setGaIdentityFromSignup = (input: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  accountName?: string | null;
  organization?: string | null;
  userId?: string | null;
}): GaIdentity =>
  setGaIdentity({
    first_name: input.firstName ?? undefined,
    last_name: input.lastName ?? undefined,
    email: input.email ?? undefined,
    account_name: input.accountName ?? undefined,
    organization: input.organization ?? undefined,
    clerk_user_id: input.userId ?? undefined,
  });

export const clearGaIdentity = (): void => {
  try {
    sessionStorage.removeItem(IDENTITY_STORAGE);
  } catch {
    // private mode
  }
};

/** Apply handoff puts email / website on the Clerk URL — pull them before sign_up. */
export const persistGaIdentityFromLocation = (): void => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const email = params.get('email') ?? undefined;
  const firstName = params.get('first_name') ?? undefined;
  const lastName = params.get('last_name') ?? undefined;
  const accountName = params.get('account_name') ?? undefined;
  const website = params.get('website_url');
  let fromWebsite: string | undefined;
  if (website) {
    try {
      fromWebsite = new URL(website).hostname.replace(/^www\./, '');
    } catch {
      fromWebsite = undefined;
    }
  }
  setGaIdentity({
    email,
    first_name: firstName,
    last_name: lastName,
    account_name: accountName ?? fromWebsite,
  });
};

function identityEventParams(): Record<string, string> {
  const identity = readGaIdentity();
  const params: Record<string, string> = {};
  for (const key of IDENTITY_KEYS) {
    const value = identity[key];
    if (value) params[key] = value;
  }
  return params;
}

export const resolveGaProduct = (): GaProduct => {
  persistGaProductFromLocation();
  try {
    const stored = sessionStorage.getItem(PRODUCT_STORAGE);
    if (stored === 'crm' || stored === 'payfac') return stored;
  } catch {
    // private mode
  }
  if (typeof window !== 'undefined' && pathImpliesPayfac(window.location.pathname)) {
    return 'payfac';
  }
  return 'crm';
};

export const gaAcquisitionParams = (
  extra?: Record<string, string>,
): Record<string, string> => {
  const product = resolveGaProduct();
  persistGaIdentityFromLocation();
  return {
    product,
    funnel: product === 'payfac' ? 'payfac_apply' : 'crm_signup',
    surface: 'app_tagada_io',
    ...identityEventParams(),
    ...extra,
  };
};

let didInit = false;

export const initGa = (): void => {
  if (typeof document === 'undefined' || didInit || !shouldTrackGa()) return;
  didInit = true;
  persistGaProductFromLocation();
  persistGaIdentityFromLocation();

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    ((...args: unknown[]) => {
      window.dataLayer?.push(args);
    });
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

export const trackGaPageView = (pathname: string): void => {
  if (!shouldTrackGa() || typeof window.gtag !== 'function') return;
  persistGaProductFromLocation();
  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_location: sanitizeGaLocation(window.location.href),
    page_title: document.title,
    ...gaAcquisitionParams(),
  });
};

/** Clerk account created. Deduped per tab — same guard idea as the X pixel. */
export const trackGaSignUp = (extra?: Record<string, string>): void => {
  if (!shouldTrackGa() || typeof window.gtag !== 'function') return;
  try {
    if (sessionStorage.getItem(SIGNUP_GUARD) === '1') return;
    sessionStorage.setItem(SIGNUP_GUARD, '1');
  } catch {
    // fire anyway
  }
  window.gtag('event', 'sign_up', gaAcquisitionParams(extra));
};
